import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormRenderer } from '@kevlarcon/formkit';
import { formKitClient } from '../../api/formkitClient';
import { partnersApi } from '../../api/partners';
import '../../styles/formkitTheme.css';

type Load =
  | { status: 'loading' }
  | { status: 'ready'; formId: number }
  | { status: 'has-listing' }
  | { status: 'error' };

/**
 * The `PartnerJoinForm` registered section type (see
 * tenant_site/section_types.py, tenant_site/system_pages.py's PartnerJoinPage)
 * — the locked, non-deletable second section on the Partner-With-Us System
 * Page. No admin-editable `data`: this is a public self-service "join the
 * partner directory" embed, the same pre-account path
 * PartnerViewSet.submit_submission (formkit's own `'public'`-role action)
 * already serves for staff-facing tooling (see AddPartnerModal). Unlike that
 * modal, this uses the real, unoverridden `formKitClient`: a successful
 * submission should land `status='submitted'` (formkit's default) and go
 * through staff review, not `imported` like a bulk-added row with no
 * applicant behind it.
 */
export default function PartnerJoinFormSection() {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    partnersApi
      .getPublicForm()
      .then(async (form) => {
        // A school may own only one listing (see PartnerFormType.
        // check_create_submission), so one that already has it is sent to its
        // console rather than shown a form the server would refuse. Tenant staff
        // are admitted to this page but aren't schools (`mine` is a 403 for
        // them) — they just see the form.
        const mine = await partnersApi.mine(form.id).catch(() => null);
        if (cancelled) return;
        setLoad(
          mine?.listing || mine?.claimable
            ? { status: 'has-listing' }
            : { status: 'ready', formId: form.id },
        );
      })
      .catch(() => {
        if (!cancelled) setLoad({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="ocen-formkit-theme">
      {load.status === 'loading' && (
        <p data-testid="partner-join-loading">Loading…</p>
      )}
      {load.status === 'error' && (
        <p data-testid="partner-join-error">
          This page isn't available right now. Please try again later.
        </p>
      )}
      {load.status === 'has-listing' && (
        <div className="wrap" style={{ padding: '2rem 1rem' }} data-testid="partner-join-has-listing">
          <p>Your school already has a listing.</p>
          <Link to="/partners/dashboard" className="btn btn-primary" data-testid="partner-join-console-link">
            Go to your dashboard
          </Link>
        </div>
      )}
      {load.status === 'ready' && (
        <FormRenderer
          client={formKitClient}
          formId={load.formId}
          variant="public"
          onSubmitted={() => navigate('/partners/dashboard')}
        />
      )}
    </div>
  );
}
